import {
  computed,
  inject,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { doc, docData, Firestore } from '@angular/fire/firestore';
import {
  Event,
  eventConverter,
  EventPaymentEnum,
} from 'src/app/models/event.model';
import { eventCost } from 'src/app/helpers/eventCost';
import { isEventLocked } from 'src/app/helpers/isEventLocked';

@Injectable()
export class EventStoreService {
  private firestore = inject(Firestore);
  private _event: WritableSignal<Event | undefined> = signal<Event | undefined>(
    undefined,
  );
  event: Signal<Event | undefined> = this._event.asReadonly();
  EventPaymentEnum: typeof EventPaymentEnum = EventPaymentEnum;

  getById(id: string) {
    const eventRef = doc(this.firestore, `events/${id}`).withConverter(
      eventConverter,
    );
    docData<Event>(eventRef, { idField: 'id' }).subscribe((event) => {
      return this._event.set(event);
    });
  }

  isEventPaid() {
    const event = this._event() as Event;
    return event.amountPaid && eventCost(event) <= event.amountPaid;
  }

  isLocked: Signal<boolean> = computed(() => {
    const event = this._event();
    return event ? isEventLocked(event) : false;
  });

  isKioskAllowed() {
    const event = this._event() as Event;
    if (!event || event.paymentType == EventPaymentEnum.CARD_ONLY) {
      return false;
    }
    const GRACE_MS = 12 * 60 * 60 * 1000;

    const start =
      event.startTime instanceof Date
        ? event.startTime.getTime()
        : new Date(event.startTime).getTime();
    const end =
      event.endTime instanceof Date
        ? event.endTime.getTime()
        : new Date(event.endTime).getTime();
    const current = new Date().getTime();

    // Guard against invalid dates / inverted ranges
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
      return false;

    const withinWindow =
      current >= start - GRACE_MS && current <= end + GRACE_MS;

    return withinWindow && this.isEventPaid();
  }
}
